import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { apiGet, apiPost } from "../../utils/apiFetch";
import apiPath from "../../utils/apiPath";
import AuthContext from "context/AuthContext";
import OButton from "components/reusable/OButton";
import useToastContext from "hooks/useToastContext";
import MFASetupModal from "components/mfa/MFASetupModal";
import Authenticator from "components/mfa/Authenticator";
import { useMFA } from "context/MFAContext";

const MFAProfile = () => {
  const { t } = useTranslation();
  const { user, updatePageName } = useContext(AuthContext);
  const notification = useToastContext();
  const { requireMfa } = useMFA();

  const [mfaStatus, setMfaStatus] = useState(null);
  const [showSetup, setShowSetup] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [showRegenerateCodes, setShowRegenerateCodes] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    updatePageName(t("MFA_SETTINGS"));
    fetchMFAStatus();
  }, []);

  const fetchMFAStatus = async () => {
    try {
      const { data } = await apiGet(apiPath.mfaStatus);
      if (data?.success) {
        setMfaStatus(data.data);
      }
    } catch (err) {
      console.error("Failed to fetch MFA status:", err);
    }
  };

  const handleRegenerateCodes = async () => {
    setLoading(true);
    try {
      const { data } = await apiPost(apiPath.mfaRecoveryCodesRegenerate, {});
      if (data?.success) {
        setRecoveryCodes(data.data.recoveryCodes || []);
        setShowRegenerateCodes(true);
        notification.success(data.message || "Recovery codes regenerated.");
      } else {
        notification.error(data?.message || "Failed to regenerate codes.");
      }
    } catch (err) {
      notification.error(
        err?.response?.data?.message || "Failed to regenerate codes.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDisableMFA = async () => {
    // Require MFA verification before disabling
    const mfaResult = await requireMfa({
      title: "Disable Two-Factor Authentication",
      description: "Enter your authenticator code to confirm disabling MFA.",
      context: "mfa:disable",
    });

    if (!mfaResult) return;

    try {
      const { data } = await apiPost(apiPath.mfaDisable, {
        stepUpProof: mfaResult.stepUpProof,
      });

      if (data?.success) {
        notification.success("MFA has been disabled.");
        setShowDisableConfirm(false);
        fetchMFAStatus();
      } else {
        notification.error(data?.message || "Failed to disable MFA.");
      }
    } catch (err) {
      notification.error(
        err?.response?.data?.message || "Failed to disable MFA.",
      );
    }
  };

  return (
    <div className="bg-[#F9F9F9]">
      <div className="py-2">
        <div className="bg-white border border-[#E9EDF9] rounded-lg py-6 px-6">
          <div className="max-w-[97%] mx-auto">
            {/* MFA Status Card */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold mb-4">
                Two-Factor Authentication
              </h3>

              {!mfaStatus ? null : !mfaStatus.mfaEnabled ? (
                /* MFA Disabled State */
                <div className="text-center py-8">
                  <div className="w-24 h-24 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                    <svg
                      className="w-12 h-12 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                  </div>
                  <p className="text-gray-600 mb-6 me-6 ms-6">
                    Add an extra layer of security to your account with
                    two-factor authentication. This will require a 6-digit code
                    from your authenticator app when logging in.
                  </p>
                  <OButton
                    onClick={() => setShowSetup(true)}
                    label="Enable MFA"
                    extraClasses="bg-LightBlue hover:bg-DarkBlue"
                  />
                </div>
              ) : (
                /* MFA Enabled State */
                <div>
                  <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
                        <svg
                          className="w-6 h-6 text-green-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="font-semibold text-green-800">
                          MFA is Enabled
                        </p>
                        <p className="text-sm text-green-600">
                          Your account is protected
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    {/* Status Details */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-medium mb-3">Status Details</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Status:</span>
                          <span className="font-medium text-green-600">
                            Active
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Enabled Since:</span>
                          <span className="font-medium">
                            {mfaStatus.enabledAt
                              ? new Date(
                                  mfaStatus.enabledAt,
                                ).toLocaleDateString()
                              : "N/A"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">
                            Recovery Codes Left:
                          </span>
                          <span className="font-medium">
                            {mfaStatus.recoveryCodesRemaining || 0}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Recovery Codes */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-medium mb-3">Recovery Codes</h4>
                      <p className="text-sm text-gray-600 mb-3">
                        Use these codes if you lose access to your authenticator
                        app.
                      </p>
                      <OButton
                        onClick={handleRegenerateCodes}
                        label="Regenerate Codes"
                        variant="outline"
                        loading={loading}
                        extraClasses="text-sm"
                      />
                    </div>
                  </div>

                  {/* Disable MFA */}
                  <div className="border-t pt-6">
                    <h4 className="font-medium mb-3 text-red-600">
                      Danger Zone
                    </h4>
                    <p className="text-sm text-gray-600 mb-3">
                      Disabling MFA will make your account less secure. You'll
                      need to enter your current authenticator code to confirm.
                    </p>
                    <OButton
                      onClick={() => setShowDisableConfirm(true)}
                      label="Disable MFA"
                      extraClasses="bg-red-500 hover:bg-red-600"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* MFA Setup Modal */}
      <MFASetupModal
        isOpen={showSetup}
        onClose={() => setShowSetup(false)}
        onEnabled={() => {
          notification.success("MFA enabled successfully.");
          setShowSetup(false);
          fetchMFAStatus();
        }}
      />

      {/* Disable Confirmation Modal */}
      <Authenticator
        isOpen={showDisableConfirm}
        onClose={() => setShowDisableConfirm(false)}
        onVerify={async (code) => {
          try {
            const { data } = await apiPost(apiPath.mfaVerify, {
              token: code,
              context: "mfa:disable",
            });
            if (data?.success) {
              return { verified: true, stepUpProof: data.data.stepUpProof };
            }
            return { verified: false, message: data.message };
          } catch (err) {
            return { verified: false, message: err?.response?.data?.message };
          }
        }}
        onSuccess={async (result) => {
          try {
            const { data } = await apiPost(apiPath.mfaDisable, {
              stepUpProof: result.stepUpProof,
            });
            if (data?.success) {
              notification.success("MFA has been disabled.");
              setShowDisableConfirm(false);
              fetchMFAStatus();
            } else {
              notification.error(data?.message || "Failed to disable MFA.");
            }
          } catch (err) {
            notification.error(
              err?.response?.data?.message || "Failed to disable MFA.",
            );
          }
        }}
        title="Disable Two-Factor Authentication"
        description="Enter your authenticator code to confirm disabling MFA."
        allowRecoveryCode
      />

      {/* Recovery Codes Display Modal */}
      {showRegenerateCodes && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              aria-hidden="true"
            >
              <div
                className="absolute inset-0 bg-gray-500 opacity-75"
                onClick={() => setShowRegenerateCodes(false)}
              ></div>
            </div>
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="text-center sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Your New Recovery Codes
                  </h3>
                  <p className="mt-2 text-sm text-gray-500">
                    Store these somewhere safe. Each code can be used once if
                    you lose access to your authenticator app.
                  </p>
                  <div className="mt-4 grid grid-cols-2 gap-2 bg-gray-50 rounded-lg p-4 font-mono text-sm">
                    {recoveryCodes.map((code, index) => (
                      <span key={index} className="text-center">
                        {code}
                      </span>
                    ))}
                  </div>
                  <div className="mt-4 flex justify-center">
                    <OButton
                      onClick={() => {
                        navigator.clipboard?.writeText(
                          recoveryCodes.join("\n"),
                        );
                        notification.success(
                          "Recovery codes copied to clipboard.",
                        );
                      }}
                      label="Copy Codes"
                      variant="outline"
                      extraClasses="text-sm mr-2"
                    />
                    <OButton
                      onClick={() => setShowRegenerateCodes(false)}
                      label="Done"
                      extraClasses="bg-LightBlue hover:bg-DarkBlue"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MFAProfile;
